import React, { useMemo, useState, useCallback } from "react";

import { motion, AnimatePresence } from "motion/react";

import { useInView } from "react-intersection-observer";

import { useProduct } from "./useProduct";

import { BackInStock } from "~/components/Product/BackInStock/BackInStock";

import { AddToCartButton } from "./AddToCartButton";

import { VariantSelector } from "./VariantSelector";

import { FindInStoreSheet } from "./FindInStoreSheet";

import { FootbedButton } from "./FootbedButton";

import { SizeButton } from "./SizeButton";

import type { ServerDataSuccess } from "./route";

export interface AddToCartStackProps {
  /** Biomechanical footbed sheet content for the product */
  biomechanicalFootbed: ServerDataSuccess["biomechanicalFootbedSheetContents"];
  /** Country code to use for the store availability */
  countryCode?: string;
  /** Enable the floating "add to cart" bar when button not in view */
  enableFloatingBar: boolean;
  /** Gender of the product */
  gender: "Mens" | "Womens";
  /** Sheet footer content to use for all sheets */
  sheetFooterContent: ServerDataSuccess["template"]["sheetFooterContent"];
  /** Size guide sheet content for the product */
  sizeGuide: ServerDataSuccess["sizeGuideSheetContents"];
}

export function AddToCartStack({
  biomechanicalFootbed,
  countryCode = "AU",
  enableFloatingBar = false,
  gender,
  sheetFooterContent,
  sizeGuide,
}: AddToCartStackProps) {
  const { productVariants } = useProduct();

  const [selectedVariantId, setSelectedVariantId] = useState<string | null>(
    1 === productVariants.length ? productVariants[0].id : null,
  );

  const selectedVariant = useMemo(() => {
    if (!selectedVariantId) return null;

    return (
      productVariants.find(
        (productVariant) => productVariant.id === selectedVariantId,
      ) ?? null
    );
  }, [selectedVariantId]);

  const [backInStockIsOpen, setBackInStockIsOpen] = useState(false);
  const onBackInStockOpen = useCallback(() => setBackInStockIsOpen(true), []);
  const [showEmptyVariantError, setShowEmptyVariantError] = useState(false);
  const [shakeKey, setShakeKey] = useState(0);
  const [showFloatingBar, setShowFloatingBar] = useState(false);

  const setVariantSelectorError = useCallback(() => {
    if (!selectedVariant) {
      setShowEmptyVariantError(true);
      setShakeKey((prev) => prev + 1);
    } else {
      setShowEmptyVariantError(false);
    }
  }, [selectedVariant]);

  // Determine if the primary "Add to cart" button is in view.
  const { ref } = useInView({
    /**
     * Shrink the root so that the target is treated as "gone" when it goes under
     * the fixed header, rather than out of the viewport.
     */
    rootMargin: "-96px 0px 0px 0px",
    // Update floating bar state.
    onChange(inView) {
      setShowFloatingBar(
        // Primary "Add to cart" is in view, don't show.
        inView
          ? false
          : // Primary "Add to cart" not in view, but we are at the top, don't show.
            200 > window.scrollY
            ? false
            : // Show the floating bar.
              true,
      );
    },
  });

  return (
    <>
      <div className="flex flex-col gap-mini" ref={ref}>
        <div className="flex justify-between">
          <FindInStoreSheet
            selectedVariant={selectedVariant}
            countryCode={countryCode}
            setVariantSelectorError={setVariantSelectorError}
            sheetFooterContent={sheetFooterContent}
          />
          {gender === "Mens" || gender === "Womens" ? (
            <SizeButton
              gender={gender}
              sizeGuide={sizeGuide}
              sheetFooterContent={sheetFooterContent}
              biomechanicalFootbed={biomechanicalFootbed}
            />
          ) : null}
        </div>

        {!selectedVariant && showEmptyVariantError && (
          <div
            key={shakeKey}
            className="animate__animated animate__shakeX text-hint text-warning"
          >
            Please select a size.
          </div>
        )}

        {1 < productVariants.length && (
          <VariantSelector
            onChange={setSelectedVariantId}
            productVariants={productVariants}
            selectedVariantId={selectedVariantId}
          />
        )}

        <AddToCartButton
          selectedVariant={selectedVariant}
          onBackInStockOpen={onBackInStockOpen}
        />
        {gender === "Mens" || gender === "Womens" ? (
          <FootbedButton
            gender={gender}
            sizeGuide={sizeGuide}
            sheetFooterContent={sheetFooterContent}
            biomechanicalFootbed={biomechanicalFootbed}
          />
        ) : null}
      </div>
      {/* When primary "Add to cart" button not in view, show floating bar on mobile */}
      {enableFloatingBar && (
        <AnimatePresence>
          {showFloatingBar && (
            <motion.div
              animate={{ y: 0 }}
              exit={{ y: "-100%" }}
              initial={{ y: "-100%" }}
              transition={{ bounce: 0 }}
              className="fixed left-zero right-zero top-[96px] z-20 flex bg-white md:hidden"
            >
              <VariantSelector
                size="md"
                className="flex-1"
                onChange={setSelectedVariantId}
                productVariants={productVariants}
                selectedVariantId={selectedVariantId}
              />
              <AddToCartButton
                size="medium"
                selectedVariant={selectedVariant}
                onBackInStockOpen={onBackInStockOpen}
              />
            </motion.div>
          )}
        </AnimatePresence>
      )}
      {!!selectedVariant && "NOTIFY" === selectedVariant.status && (
        <BackInStock
          active={backInStockIsOpen}
          setActive={setBackInStockIsOpen}
          selectedVariant={selectedVariant}
        />
      )}
    </>
  );
}
